import { t, useI18n } from '../../i18n';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Source, Layer } from '@vis.gl/react-maplibre';
import type { MapRef } from '@vis.gl/react-maplibre';
import MapLibreMap from '../../components/maps/MapLibreMap';
import MapPinMarker from '../../components/maps/MapPinMarker';
import { createCircleFeature } from '../../utils/geo';

const DEFAULT_CENTER: [number, number] = [51.505, -0.09];
const DEFAULT_ZOOM = 5;
const SITE_ZOOM = 17;
const PICKED_ZOOM = 17;
const MIN_RADIUS = 10;

function toInitialViewState(center: [number, number], zoom: number) {
  return {
    latitude: center[0],
    longitude: center[1],
    zoom,
  };
}

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'User-Agent': 'OdinsEyeAdminPortal/1.0' } },
    );
    if (!res.ok) throw new Error('Nominatim error');
    const data = await res.json() as { display_name?: string };
    return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

interface InlineMapPickerProps {
  initialCenter?: [number, number] | null;
  markerPos?: [number, number] | null;
  onMapClick: (lat: number, lng: number) => void;
  geofenceRadius?: number | null;
}

export function InlineMapPicker({ initialCenter, markerPos, onMapClick, geofenceRadius }: InlineMapPickerProps) {
  useI18n();
  const mapRef = useRef<MapRef | null>(null);
  const startCenter = initialCenter ?? DEFAULT_CENTER;
  const startZoom = initialCenter ? SITE_ZOOM : DEFAULT_ZOOM;
  const effectiveRadius = Math.max(MIN_RADIUS, Number.parseInt(String(geofenceRadius), 10) || MIN_RADIUS);
  const geofence = useMemo(
    () => (markerPos ? createCircleFeature(markerPos, effectiveRadius) : null),
    [markerPos, effectiveRadius],
  );

  useEffect(() => {
    if (!mapRef.current || !markerPos) return;
    mapRef.current.flyTo({
      center: [markerPos[1], markerPos[0]],
      zoom: PICKED_ZOOM,
      duration: 1000,
    });
  }, [markerPos]);

  return (
    <MapLibreMap
      ref={mapRef}
      initialViewState={toInitialViewState(startCenter, startZoom)}
      style={{ height: '100%', width: '100%' }}
      onClick={(event: { lngLat: { lat: number; lng: number } }) => onMapClick(event.lngLat.lat, event.lngLat.lng)}
    >
      {geofence && (
        <Source id="inline-map-picker-geofence" type="geojson" data={geofence}>
          <Layer
            id="inline-map-picker-geofence-fill"
            type="fill"
            paint={{ 'fill-color': '#4f8ef7', 'fill-opacity': 0.15 }}
          />
          <Layer
            id="inline-map-picker-geofence-line"
            type="line"
            paint={{ 'line-color': '#4f8ef7', 'line-width': 2, 'line-opacity': 0.8 }}
          />
        </Source>
      )}

      {markerPos && (
        <MapPinMarker
          position={markerPos}
          color="#4f8ef7"
          title={t('Selected location')}
        />
      )}
    </MapLibreMap>
  );
}

interface MapPickerModalProps {
  onConfirm: (lat: number, lng: number, address: string, radius?: number) => void;
  onClose: () => void;
  initialCenter?: [number, number] | null;
  geofenceRadius?: number | null;
}

export default function MapPickerModal({ onConfirm, onClose, initialCenter, geofenceRadius }: MapPickerModalProps) {
  useI18n();
  const mapRef = useRef<MapRef | null>(null);
  const showGeofence = geofenceRadius != null;
  const startCenter = initialCenter ?? DEFAULT_CENTER;
  const startZoom = initialCenter ? SITE_ZOOM : DEFAULT_ZOOM;

  const [markerPos, setMarkerPos] = useState<[number, number] | null>(null);
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [radius, setRadius] = useState<number | string>(geofenceRadius ?? 50);
  const [geoCenter, setGeoCenter] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!initialCenter && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeoCenter([pos.coords.latitude, pos.coords.longitude]),
        () => {},
      );
    }
  }, [initialCenter]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (markerPos) {
      mapRef.current.flyTo({
        center: [markerPos[1], markerPos[0]],
        zoom: PICKED_ZOOM,
        duration: 1000,
      });
      return;
    }

    if (!initialCenter && geoCenter) {
      mapRef.current.flyTo({
        center: [geoCenter[1], geoCenter[0]],
        zoom: 14,
        duration: 1200,
      });
    }
  }, [geoCenter, initialCenter, markerPos]);

  const handleMapClick = useCallback(async (event: { lngLat: { lat: number; lng: number } }) => {
    const nextMarker: [number, number] = [event.lngLat.lat, event.lngLat.lng];
    setMarkerPos(nextMarker);
    setIsResolving(true);
    setResolvedAddress('');
    const address = await reverseGeocode(nextMarker[0], nextMarker[1]);
    setResolvedAddress(address);
    setIsResolving(false);
  }, []);

  const effectiveRadius = Math.max(MIN_RADIUS, Number.parseInt(String(radius), 10) || MIN_RADIUS);
  const geofence = useMemo(
    () => (markerPos && showGeofence ? createCircleFeature(markerPos, effectiveRadius) : null),
    [markerPos, showGeofence, effectiveRadius],
  );

  const handleConfirm = () => {
    if (!markerPos) return;
    if (showGeofence) {
      onConfirm(markerPos[0], markerPos[1], resolvedAddress, effectiveRadius);
    } else {
      onConfirm(markerPos[0], markerPos[1], resolvedAddress);
    }
  };

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal map-picker__modal">
        <div className="modal__header">
          <h3 className="modal__title">{t('Pick Location on Map')}</h3>
          <button className="btn btn--icon" onClick={onClose}>&times;</button>
        </div>

        <p className="map-picker__hint">{t('Click anywhere on the map to drop a pin.')}</p>

        {showGeofence && (
          <div className="map-picker__radius-row">
            <label className="map-picker__radius-label">{t('Geofence Radius (metres)')}</label>
            <input
              type="number"
              min={MIN_RADIUS}
              step="1"
              value={radius}
              onChange={(event) => setRadius(event.target.value)}
              className="form__input map-picker__radius-input"
            />
          </div>
        )}

        <div className="map-picker__map">
          <MapLibreMap
            ref={mapRef}
            initialViewState={toInitialViewState(startCenter, startZoom)}
            style={{ height: '100%', width: '100%' }}
            onClick={handleMapClick}
          >
            {geofence && (
              <Source id="modal-map-picker-geofence" type="geojson" data={geofence}>
                <Layer
                  id="modal-map-picker-geofence-fill"
                  type="fill"
                  paint={{ 'fill-color': '#4f8ef7', 'fill-opacity': 0.15 }}
                />
                <Layer
                  id="modal-map-picker-geofence-line"
                  type="line"
                  paint={{ 'line-color': '#4f8ef7', 'line-width': 2, 'line-opacity': 0.8 }}
                />
              </Source>
            )}

            {markerPos && (
              <MapPinMarker
                position={markerPos}
                color="#4f8ef7"
                title={t('Selected location')}
              />
            )}
          </MapLibreMap>
        </div>

        <div className="map-picker__status">
          {isResolving && t('Resolving address...')}
          {!isResolving && resolvedAddress && resolvedAddress}
        </div>

        <div className="map-picker__actions">
          <button className="btn btn--secondary" onClick={onClose}>{t('Cancel')}</button>
          <button
            className="btn btn--primary"
            disabled={!markerPos || isResolving}
            onClick={handleConfirm}
          >
            {t('Confirm Location')}
          </button>
        </div>
      </div>
    </div>
  );
}
